import React, { useState, useEffect } from 'react';
import { X, Plus, Check, Music } from 'lucide-react';
import dataManager from '../utils/dataManager';

const PlaylistSelector = ({ isOpen, onClose, song, onSuccess }) => {
  const [playlists, setPlaylists] = useState([]);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPlaylists();
    }
  }, [isOpen]);

  const loadPlaylists = () => {
    const playlistData = dataManager.getPlaylists();
    setPlaylists(playlistData);
  };

  const handleAddToPlaylist = (playlistId) => {
    const success = dataManager.addSongToPlaylist(playlistId, song);
    if (success) {
      const playlist = playlists.find(p => p.id === playlistId);
      onSuccess && onSuccess(playlist.name);
      onClose();
    } else {
      // 歌曲已存在于歌单中
      alert('该歌曲已在歌单中！');
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    
    setIsCreating(true);
    try {
      const newPlaylist = dataManager.createPlaylist(newPlaylistName.trim());
      const success = dataManager.addSongToPlaylist(newPlaylist.id, song);
      
      if (success) {
        onSuccess && onSuccess(newPlaylist.name);
        onClose();
        setNewPlaylistName('');
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('创建歌单失败:', error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark-50 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-200">
          <div>
            <h3 className="text-lg font-semibold text-dark-800">添加到歌单</h3>
            <p className="text-sm text-dark-500 truncate mt-1">
              {song?.name} - {song?.artist}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-100 rounded-lg transition-colors duration-200"
          >
            <X size={20} className="text-dark-600" />
          </button>
        </div>

        {/* 歌单列表 */}
        <div className="max-h-96 overflow-y-auto scrollbar-thin">
          <div className="p-4 space-y-2">
            {/* 创建新歌单按钮 */}
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="w-full flex items-center gap-3 p-3 hover:bg-dark-100 rounded-xl transition-colors duration-200 text-left"
            >
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
                <Plus size={24} className="text-white" />
              </div>
              <div>
                <h4 className="font-medium text-dark-800">创建新歌单</h4>
                <p className="text-sm text-dark-500">添加到新的歌单</p>
              </div>
            </button>

            {/* 创建歌单表单 */}
            {showCreateForm && (
              <div className="bg-dark-100 rounded-xl p-4 space-y-3">
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder="输入歌单名称..."
                  className="w-full px-3 py-2 bg-dark-50 border border-dark-200 rounded-lg text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleCreatePlaylist()}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreatePlaylist}
                    disabled={!newPlaylistName.trim() || isCreating}
                    className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isCreating ? '创建中...' : '创建并添加'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewPlaylistName('');
                    }}
                    className="px-4 py-2 bg-dark-200 text-dark-700 rounded-lg font-medium hover:bg-dark-300 transition-colors duration-200"
                  >
                    取消
                  </button>
                </div>
              </div>
            )}

            {/* 现有歌单列表 */}
            {playlists.length > 0 ? (
              playlists.map((playlist) => (
                <button
                  key={playlist.id}
                  onClick={() => handleAddToPlaylist(playlist.id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-dark-100 rounded-xl transition-colors duration-200 text-left group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center">
                    <Music size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-dark-800 truncate group-hover:text-primary-600 transition-colors duration-200">
                      {playlist.name}
                    </h4>
                    <p className="text-sm text-dark-500 truncate">
                      {playlist.songs.length} 首歌曲
                    </p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                      <Plus size={14} className="text-white" />
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <Music size={48} className="text-dark-300 mx-auto mb-3" />
                <p className="text-dark-500">还没有歌单</p>
                <p className="text-sm text-dark-400">创建第一个歌单开始收集音乐</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistSelector;
